import { serverDateSort } from 'components/Tables/dateSorting';
import React, { useCallback, useRef } from 'react';
import moment from 'moment';
import { useMutation } from '@apollo/client';
import { Button, Popconfirm, Space, Tag, Tooltip, notification } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import CCCTable from '../../../../components/CCCTable/CCCTable';
import {
  MUTATION_RESET_DEVICE,
  MUTATION_SET_DEVICE_DISABLED,
  QUERY_LIST_DEVICE,
  QUERY_TOTAL_DEVICE,
} from '../../../../queries/DeviceQueries';

interface StationDevicesTabProps {
  corpClientId: number;
}

interface StationDevice {
  device_id: string;
  device_name?: string | null;
  serial_number?: string | null;
  esper_device_id?: string | null;
  location_name?: string | null;
  station_mode?: string | null;
  locked_role?: string | null;
  lock_status: 'LOCKED' | 'NOT_LOCKED' | 'DISABLED' | 'REPLACED';
  shared_location_count: number;
  reset_at?: string | null;
  reset_source?: string | null;
}

const STATUS_TAGS: Record<StationDevice['lock_status'], [string, string]> = {
  LOCKED: ['green', 'Locked'],
  NOT_LOCKED: ['default', 'Not locked'],
  DISABLED: ['red', 'Disabled'],
  REPLACED: ['default', 'Replaced'],
};

// Plain words for station_config.mode, as on the tablet's confirmation screen.
const MODE_LABELS: Record<string, string> = {
  tablet_checkout: 'Tablet checkout',
  hid_checkout: 'HID checkout',
  boh_address_lookup: 'Address lookup',
  boh_illumia_order: 'Order Ahead',
  boh_grubhub_card: 'Grubhub card',
  tablet_return: 'Tablet return',
  hid_return: 'HID return',
};

const deviceLabel = (device: StationDevice) =>
  device.device_name || device.serial_number || device.device_id.slice(0, 8);

const deviceWhere = (device: StationDevice) =>
  device.location_name ? ` at ${device.location_name}` : '';

const capitalize = (value: string) =>
  `${value[0].toUpperCase()}${value.slice(1)}`;

// Esper kiosk lock (CCC-3129): each tablet's location and mode, with Reset (back to setup on
// the tablet) and Disable. The tablet applies either on its next check, within a few minutes.
const StationDevicesTab: React.FC<StationDevicesTabProps> = ({
  corpClientId,
}) => {
  const tableRef = useRef<any>(null);

  const onError = useCallback((error: Error) => {
    notification.error({ message: 'Error', description: error.message });
  }, []);

  const [resetDevice] = useMutation(MUTATION_RESET_DEVICE, { onError });
  const [setDeviceDisabled] = useMutation(MUTATION_SET_DEVICE_DISABLED, {
    onError,
  });

  const handleReset = useCallback(
    async (device: StationDevice) => {
      const { data } = await resetDevice({
        variables: { device_id: device.device_id },
      });
      if (data) {
        notification.success({
          message: 'Tablet reset',
          description: `${deviceLabel(
            device,
          )} returns to setup on its next check.`,
        });
        tableRef.current?.refetch();
      }
    },
    [resetDevice],
  );

  const handleSetDisabled = useCallback(
    async (device: StationDevice, disabled: boolean) => {
      const { data } = await setDeviceDisabled({
        variables: { device_id: device.device_id, disabled },
      });
      if (data) {
        notification.success({
          message: disabled ? 'Tablet disabled' : 'Tablet enabled',
          description: deviceLabel(device),
        });
        tableRef.current?.refetch();
      }
    },
    [setDeviceDisabled],
  );

  const columns = [
    {
      title: 'Tablet',
      key: 'device',
      render: (_: unknown, device: StationDevice) => (
        <Space direction="vertical" size={0}>
          <span>{deviceLabel(device)}</span>
          {device.serial_number && device.device_name ? (
            <small>Serial {device.serial_number}</small>
          ) : null}
          {device.esper_device_id ? (
            <small>Esper {device.esper_device_id}</small>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      render: (_: unknown, device: StationDevice) =>
        device.location_name ? (
          <Space>
            <span>{device.location_name}</span>
            {device.shared_location_count > 0 ? (
              <Tooltip
                title={`${device.shared_location_count} other tablet(s) are locked to this location. Sometimes intended, often a mistake.`}
              >
                <Tag color="orange" icon={<WarningOutlined />}>
                  Shared
                </Tag>
              </Tooltip>
            ) : null}
          </Space>
        ) : (
          '-'
        ),
    },
    {
      title: 'Role / Mode',
      key: 'mode',
      render: (_: unknown, device: StationDevice) => {
        const role = device.locked_role ? capitalize(device.locked_role) : null;
        const mode = device.station_mode
          ? MODE_LABELS[device.station_mode] || device.station_mode
          : null;
        return [role, mode].filter(Boolean).join(' / ') || '-';
      },
    },
    {
      title: 'Status',
      key: 'lock_status',
      render: (_: unknown, device: StationDevice) => {
        const [color, label] = STATUS_TAGS[device.lock_status];
        const resetNote =
          device.lock_status === 'NOT_LOCKED' && device.reset_at
            ? `Reset ${moment(device.reset_at).format('LLL')} ${
                device.reset_source === 'tablet' ? 'on the tablet' : 'in admin'
              }`
            : undefined;
        return (
          <Tooltip title={resetNote}>
            <Tag color={color}>{label}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Created At',
      ...serverDateSort(true),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => (date ? moment(date).format('LLL') : '-'),
    },
    {
      title: 'Last Active At',
      ...serverDateSort(),
      dataIndex: 'last_active_at',
      key: 'last_active_at',
      render: (date: string) => (date ? moment(date).format('LLL') : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, device: StationDevice) => {
        if (device.lock_status === 'REPLACED') return null;
        const locked = device.lock_status === 'LOCKED';
        const disabled = device.lock_status === 'DISABLED';
        return (
          <Space>
            <Popconfirm
              title={`Reset ${deviceLabel(device)}${deviceWhere(device)}?`}
              description="The tablet goes back to its setup screen. Someone with the lock code sets it up again."
              okText="Reset"
              onConfirm={() => handleReset(device)}
              disabled={!locked}
            >
              <Button size="small" disabled={!locked}>
                Reset
              </Button>
            </Popconfirm>
            <Popconfirm
              title={`${disabled ? 'Enable' : 'Disable'} ${deviceLabel(
                device,
              )}${deviceWhere(device)}?`}
              description={
                disabled
                  ? 'The tablet opens its station again on its next check.'
                  : 'The tablet shows a "disabled" screen and its station requests stop working.'
              }
              okText={disabled ? 'Enable' : 'Disable'}
              okButtonProps={{ danger: !disabled }}
              onConfirm={() => handleSetDisabled(device, !disabled)}
            >
              <Button size="small" danger={!disabled}>
                {disabled ? 'Enable' : 'Disable'}
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  if (!corpClientId) return null;

  return (
    <CCCTable
      ref={tableRef}
      rowKey="device_id"
      query={QUERY_LIST_DEVICE}
      queryKey="listDevice"
      totalQuery={QUERY_TOTAL_DEVICE}
      totalQueryKey="totalDevice"
      columns={columns}
      where={{
        corporate_client_id: { eq: corpClientId },
      }}
    />
  );
};

export default StationDevicesTab;
